import React, {useCallback, useContext, useEffect, useRef} from 'react';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {useDispatch, useSelector} from 'react-redux';
import {closeFilter, selectFilterStatus} from '../FilterSlice.jsx';
import FilterSideSheet from './large/FilterSideSheet.jsx';
import FilterAccordion from './FilterAccordion.jsx';
import FilterLayerMobile from './small/FilterLayerMobile.jsx';
import {mergeSelectedFiltersWithParams, yieldToMain} from '../ArticleOverviewHelper.js';
import {setFilterAjaxCall} from '../AjaxCallSlice.jsx';
import {loadOffFilter, loadOnFilter, selectLoadingbarStatus} from '../LoaderSlice.jsx';
import {loadData} from '../helper/data-loader.js';
import JSONParser from '@core/scripts/helper/json-parser';
import useEffectOnUpdate from '../helper/hooks-helper.jsx';
import {useBreakpointUp} from 'bonprix-design-system';
import History from '@core/scripts/components/history';
import MessageBroker from 'deprecated-shared-ui/components/message-broker';
import Topics from '@core/scripts/components/topics';
import {sendClickedFiltersEvent} from '@shopping-funnel/scripts/helper/algolia-tracking';
import {getClickedAlgoliaFilters} from '@shopping-funnel/scripts/helper/algolia-filter-context.ts';

function Filter({filterContent, filterSubmit, ordering}) {
    const {filterAjaxUrl, shopId, currentSearchParams} = useContext(articleOverviewStaticContext);

    const breakpointLUp = useBreakpointUp('l');
    const filterOpened = useSelector(selectFilterStatus).opened;
    const contentLoading = useSelector(selectLoadingbarStatus).status;

    const dispatch = useDispatch();

    const clickedSortOption = useRef(undefined);
    const currentFilters = useRef(new Map());
    const selectedFilterValues = useRef(new Map());

    useEffect(() => {
        const nextSelectedFilterValues = new Map();

        filterContent.filterGroups?.forEach((filterGroup) => {
            filterGroup.filters?.forEach((filter) => {
                const selectedValues = (filter.filterOptions || [])
                    .filter((option) => option.selected)
                    .map((option) => option.value);

                nextSelectedFilterValues.set(filter.identifier, selectedValues);
            });
        });

        selectedFilterValues.current = nextSelectedFilterValues;
    }, [filterContent.filterGroups]);

    useEffectOnUpdate(() => {
        if (!filterOpened) {
            currentFilters.current = new Map();
            clickedSortOption.current = undefined;
        }
    }, [filterOpened]);

    useEffectOnUpdate(() => {
        // close filter accordion after content load
        if (filterOpened && !contentLoading) {
            dispatch(closeFilter());
        }
    }, [contentLoading]);

    useEffect(() => {
        const messages = [
            MessageBroker.subscribe(`${Topics.HISTORY}.filterLayer`, () => {
                dispatch(setFilterAjaxCall(['', false]));
                dispatch(closeFilter());
            }),
        ];

        return () => {
            messages.forEach((message) => MessageBroker.unsubscribe(message));
        };
    }, [dispatch]);

    useEffect(() => {
        if (!breakpointLUp && filterOpened) {
            History.push('filterLayer');
        }
    }, [filterOpened, breakpointLUp]);

    const reloadOnlyFilters = async (clickedFilters, isRangeFilter) => {
        const mergedParams = mergeSelectedFiltersWithParams(clickedFilters, currentSearchParams.current);
        const shopIdFormatted = shopId.current ? `/${shopId.current}/` : '/';
        const filterAjaxCall = filterAjaxUrl + shopIdFormatted + '?' + mergedParams.toString();

        dispatch(setFilterAjaxCall([filterAjaxCall, isRangeFilter]));
    };

    const filterOptionChange = useCallback(
        async (identifier, trackingId, clickedFilters, isRange = false) => {
            dispatch(loadOnFilter());
            return await yieldToMain(async () => {
                const previouslySelectedValues = selectedFilterValues.current.get(identifier) || [];
                const filterData = {
                    trackingId: trackingId,
                    clickedFilterOptions: clickedFilters,
                };

                currentFilters.current.set(identifier, filterData);
                selectedFilterValues.current.set(identifier, clickedFilters);

                if (!isRange) {
                    const clickedAlgoliaFilters = getClickedAlgoliaFilters(identifier, previouslySelectedValues, clickedFilters);
                    sendClickedFiltersEvent(clickedAlgoliaFilters);
                }

                if (isRange) {
                    const newHits = await reloadHits(currentFilters.current);
                    if (newHits) {
                        await reloadOnlyFilters(currentFilters.current, isRange);
                    } else {
                        currentFilters.current.delete(identifier);
                        dispatch(loadOffFilter());
                    }
                    return newHits;
                }

                await reloadOnlyFilters(currentFilters.current, isRange);
            });
        },
        [currentFilters.current, reloadOnlyFilters],
    );

    function reloadHits(clickedFilters) {
        const shopIdFormatted = shopId.current ? `/${shopId.current}/` : '/';

        const currentParamsCopy = mergeSelectedFiltersWithParams(clickedFilters, currentSearchParams.current);
        const filterAjaxRequest = filterAjaxUrl + shopIdFormatted + '?' + currentParamsCopy.toString();

        const newHitsContent = loadData(filterAjaxRequest);
        return newHitsContent.then((result) => {
            if (result) {
                const modelJson = JSONParser.tryParseJSON(result.model);
                return modelJson.hits;
            }
        });
    }

    const orderingChange = (sortValue, sortLm) => {
        clickedSortOption.current = {
            value: sortValue,
            lm: sortLm,
        };
    };

    const onSubmitButtonClick = () => {
        filterSubmit(currentFilters.current, clickedSortOption.current);
    };

    const classes = breakpointLUp ? '-side-sheet' : '';

    const filterAccordion = (
        <FilterAccordion
            filterGroups={filterContent.filterGroups}
            ordering={ordering}
            filterOptionChange={filterOptionChange}
            orderingChange={orderingChange}
            classes={classes}
        />
    );

    if (breakpointLUp) {
        return (
            <FilterSideSheet hits={filterContent.hits} filterSubmit={onSubmitButtonClick}>
                {filterAccordion}
            </FilterSideSheet>
        );
    }

    if (!breakpointLUp && filterOpened) {
        return (
            <FilterLayerMobile hits={filterContent.hits} filterSubmit={onSubmitButtonClick} >
                {filterAccordion}
            </FilterLayerMobile>
        );
    }

    return <></>;
}

export default Filter;
